import { FC, Fragment, useState } from 'react';
import { FormLabel } from 'react-bootstrap';
import { connect } from 'react-bootstrap-formutil';
import CountdownToast from 'components/CountdownToast';
import { useGetIdTypeInfo } from 'components/forms/IdType';
import Toast from 'components/Toast';
import { FileInput } from 'components/Uploader';
import { OCRTypeEnum, PicProofEnum } from 'types/common';
import { IdTypeEnum } from 'types/customer';
import API from 'utils/API';
import classlist from 'utils/classlist';
import { defaultLabelFields, IDFields, ocrAllowSizeOption } from './helper';
import { UploadIDProps, UploadOcrFunc } from './type';
import './style.scss';

const UploadID: FC<UploadIDProps> = ({
    className,
    uploadFiles,
    extraLabelFields,
    ocrOptions,
    idCardInfoOptions: { idType, issueCountry } = {}
}) => {
    const { onOCRDataChange, sizeOption: ocrSizeOption = ocrAllowSizeOption } = ocrOptions || {};
    // 用于记录当前ocr请求的状态, 防止重复请求ocr数据
    const [ocrDataStatus, setOcrDataStatus] = useState<Partial<Record<IdTypeEnum, boolean>>>({});
    // 通过接口获取需要上传证件配置信息，比如是否需要正反面。
    const { data: dataIdInfo } = useGetIdTypeInfo({
        idType,
        issueCountry
    });

    const handleUploadOCR: UploadOcrFunc = (data, type) => {
        // 调用onOCRDataChange, 更新上层组件中的ocr数据
        onOCRDataChange?.(data, type);
    };

    return (
        <div className={classlist('upload-id-root', className)}>
            {uploadFiles.map(requiredFilesItem => {
                const extraFieldConfig = extraLabelFields?.[requiredFilesItem];
                const defaultFieldConfig = defaultLabelFields[requiredFilesItem];
                // 如果extraLabelFields中存在对应的label/desc/fields, 就用对应的内容, 否则使用默认值
                const curLabel = extraFieldConfig?.label || defaultFieldConfig?.label;
                const curDesc = extraFieldConfig?.desc || defaultFieldConfig?.desc;
                const curTypes = extraFieldConfig?.types || defaultFieldConfig?.types;
                const curTooltips = extraFieldConfig?.tooltips || defaultFieldConfig?.tooltips;
                const { texts: descTexts, needOrderNum } = curDesc || {};
                // 身份证明相关证件、面数从接口获得
                let fields: PicProofEnum[] | undefined = undefined;

                if (IDFields.includes(requiredFilesItem)) {
                    const sides = dataIdInfo?.sides;

                    if (sides) {
                        fields = sides === 1 ? defaultFieldConfig?.fields.slice(0, 1) : defaultFieldConfig?.fields;
                    }
                }

                const curFields = extraFieldConfig?.fields || fields || defaultFieldConfig?.fields;

                return curFields ? (
                    <Fragment key={requiredFilesItem}>
                        {curLabel && <FormLabel>{curLabel}</FormLabel>}
                        {Array.isArray(descTexts) && !!descTexts.length && (
                            <div className="label-desc">
                                {descTexts.map((descItem, descIndex) => (
                                    <p key={descIndex} className="desc-item mb-0">
                                        {needOrderNum ? `${descIndex + 1}.${descItem}` : descItem}
                                    </p>
                                ))}
                            </div>
                        )}
                        <div className="upload-fields">
                            {curFields.map((fieldName, fieldIndex) => {
                                const useOCR = !!ocrOptions;
                                const sizeOption = useOCR ? ocrSizeOption : undefined;
                                const onUploadOCR = useOCR ? handleUploadOCR : undefined;
                                const curFiled = curFields?.[fieldIndex] || fieldName;
                                const curFileUploaderType = curTypes?.[fieldIndex] || fieldName;
                                const curTooltip = curTooltips?.[fieldIndex];

                                return (
                                    <FileInput
                                        type={curFileUploaderType}
                                        field={curFiled}
                                        className="file-upload"
                                        tooltip={curTooltip}
                                        key={`${requiredFilesItem}_${fieldName}`}
                                        name={fieldName}
                                        required
                                        $parser={value => value?.url}
                                        $validators={{
                                            required: value => !!value || __('请上传证件')
                                        }}
                                        $onFieldChange={async (value, oldValue, $curFormutil) => {
                                            // 某个证件类型下的证件是否正在请求ocr数据
                                            let isGettingOCRData = !!(idType && ocrDataStatus[idType]);

                                            if (idType) {
                                                setOcrDataStatus(val => ({
                                                    ...val,
                                                    [idType]: true
                                                }));
                                            }

                                            // 非银行卡的OCR且需要上传的文件都已经上传，则去获取ocr数据
                                            if (
                                                useOCR &&
                                                ocrOptions.ocrType !== OCRTypeEnum.BANK_CARD &&
                                                !isGettingOCRData &&
                                                curFields.every(item => !!$curFormutil.$params[item])
                                            ) {
                                                const resultUrl = curFields
                                                    .map(item => $curFormutil.$params[item])
                                                    .join(',');

                                                CountdownToast.loading(true, {
                                                    countdown: 10,
                                                    content: __('正在处理中')
                                                });

                                                try {
                                                    const ocrData = await API.CUSTOMER.getOcrResult
                                                        .get({
                                                            params: {
                                                                url: resultUrl,
                                                                id_type: idType,
                                                                issue_country: issueCountry,
                                                                license: ocrOptions.license
                                                            }
                                                        })
                                                        .then(res => res.data);

                                                    await CountdownToast.loading(false);

                                                    onUploadOCR?.(ocrData, fieldName);
                                                } catch (error) {
                                                    await CountdownToast.loading(false);

                                                    Toast.show(error);
                                                }
                                            }

                                            if (idType) {
                                                setOcrDataStatus(val => ({
                                                    ...val,
                                                    [idType]: false
                                                }));
                                            }
                                        }}
                                        sizeOption={sizeOption}
                                    />
                                );
                            })}
                        </div>
                    </Fragment>
                ) : null;
            })}
        </div>
    );
};

export default connect(UploadID);
export * from './helper';
export * from './type';
